import { Zap, Recycle, BarChart3, Leaf, ArrowRight } from "lucide-react";
import StandardSection from "@/components/ui/StandardSection";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/utils/authContext";
import logger from '@/utils/logger';
import { fetchContentJson, extractPanelData, getFieldValue, getNestedPanel } from "@/utils/contentJsonParser";

interface SustainabilitySectionProps {
  base_path: string;
  isExploreEnabled?: boolean;
}

export default function SustainabilitySection({ base_path, isExploreEnabled = true }: SustainabilitySectionProps) {
  const [loading, setLoading] = useState(false);
  const [sectionTitle, setSectionTitle] = useState<string>("");
  const [sectionPrimaryHeader, setSectionPrimaryHeader] = useState<string>("");
  const [sectionSecondaryHeader, setSectionSecondaryHeader] = useState<string>("");
  const [sectionDescription, setSectionDescription] = useState<string>("");
  const [card1Title, setCard1Title] = useState<string>("");
  const [card1Description, setCard1Description] = useState<string>("");
  const [card2Title, setCard2Title] = useState<string>("");
  const [card2Description, setCard2Description] = useState<string>("");
  const [card3Title, setCard3Title] = useState<string>("");
  const [card3Description, setCard3Description] = useState<string>("");

  const { isLoggedIn } = useAuth();

  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await fetchContentJson();
        
        // Extract the high impact model panel
        const highImpactPanel = extractPanelData(data, "highImpactModelPanel");
        
        if (highImpactPanel) {
          // Extract main section fields
          setSectionTitle(getFieldValue(highImpactPanel, "carbon_title", ""));
          setSectionPrimaryHeader(getFieldValue(highImpactPanel, "carbon_primary_header", ""));
          setSectionSecondaryHeader(getFieldValue(highImpactPanel, "carbon_secondary_header", ""));
          setSectionDescription(getFieldValue(highImpactPanel, "carbon_description", ""));

          // Extract card data from nested panels
          const card1Panel = getNestedPanel(highImpactPanel, "card1Panel");
          if (card1Panel) {
            setCard1Title(getFieldValue(card1Panel, "card1_title", ""));
            setCard1Description(getFieldValue(card1Panel, "card1_description", ""));
          }

          const card2Panel = getNestedPanel(highImpactPanel, "card2Panel");
          if (card2Panel) {
            setCard2Title(getFieldValue(card2Panel, "card2_title", ""));
            setCard2Description(getFieldValue(card2Panel, "card2_description", ""));
          }

          const card3Panel = getNestedPanel(highImpactPanel, "card3Panel");
          if (card3Panel) {
            setCard3Title(getFieldValue(card3Panel, "card3_title", ""));
            setCard3Description(getFieldValue(card3Panel, "card3_description", ""));
          }
        }
        
        setLoading(false);
      } catch (error) {
        logger.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);


  return (
    <StandardSection id="sustainability" background="gradient">
      <div className="text-center space-y-8">
        {/* Button with side icon */}
        <div className="flex justify-center">
          <div className="inline-flex items-center gap-2 bg-primary-100 text-primary-700 px-3 sm:px-4 py-2 rounded-full text-sm shadow-sm mb-4">
            <Leaf className="h-4 w-4 text-green-600" />
            <span className="text-lg sm:text-xl leading-relaxed">{sectionTitle}</span>
            <Zap className="h-4 w-4 text-green-600" />
          </div>

        </div>

        {/* H2 Title */}
        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-tight">
          <span className="text-gray-900 block">
            {sectionPrimaryHeader || "Impactful Advertising."}
          </span>
          <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent block">
            {sectionSecondaryHeader || "Minimal Footprint."}
          </span>
        </h2>

        {/* Sub-heading */}
        <p className="text-lg sm:text-xl text-gray-700 leading-relaxed max-w-4xl mx-auto">
          {sectionDescription || "Welcome to the new era of Out-of-Home. Moving Walls Market is dedicated to connecting your brand with sustainable media options, making it simple to run effective campaigns that respect the planet."}
        </p>

        {/* Key Points with Icons */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 max-w-5xl mx-auto mt-12">
          <div className="flex flex-col items-center gap-4 bg-white/80 backdrop-blur-sm rounded-lg p-6 border border-green-200 shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300">
            <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center">
              <Zap className="h-8 w-8 text-green-600" />
            </div>
            <div className="text-center">
              <h3 className="text-gray-900 font-semibold text-lg mb-2">{card1Title || "Energy-Efficient Digital Networks"}</h3>
              <p className="text-gray-600 text-sm">{card1Description || "Powered by renewable energy sources and optimized for minimal power consumption"}</p>
            </div>
          </div>

          <div className="flex flex-col items-center gap-4 bg-white/80 backdrop-blur-sm rounded-lg p-6 border border-green-200 shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300">
            <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center">
              <Recycle className="h-8 w-8 text-green-600" />
            </div>
            <div className="text-center">
              <h3 className="text-gray-900 font-semibold text-lg mb-2">{card2Title || "Zero Physical Waste"}</h3>
              <p className="text-gray-600 text-sm">{card2Description || "Digital displays eliminate paper, vinyl, and other materials used in traditional advertising"}</p>
            </div>
          </div>

          <div className="flex flex-col items-center gap-4 bg-white/80 backdrop-blur-sm rounded-lg p-6 border border-green-200 shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300">
            <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center">
              <BarChart3 className="h-8 w-8 text-green-600" />
            </div>
            <div className="text-center">
              <h3 className="text-gray-900 font-semibold text-lg mb-2">{card3Title || "Precision Targeting, Fewer Resources"}</h3>
              <p className="text-gray-600 text-sm">{card3Description || "AI-driven campaigns reduce waste by targeting the right audience at the right time"}</p>
            </div>
          </div>
        </div>

        {/* Call-to-Action Button - Only show if explore is enabled */}
        {isExploreEnabled && (
          <div className="flex justify-center mt-12">
            <button
              className="inline-flex items-center gap-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white px-8 py-4 rounded-full text-lg font-semibold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
              data-testid="button-explore-sustainable-options"
              onClick={() => {
                localStorage.setItem('clearStartCampaignFilters', 'true');
                if (isLoggedIn) {
                  navigate(`${base_path}/propertylist`);
                } else {
                  navigate(`${base_path}/explore`);
                }
              }}
            >
              Explore Sustainable Options
              <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>
    </StandardSection>
  );
}